/* Header cart badge — HEllO EDC (India store).
   Rewritten 9 Sep 2026 (third pass). Read this before touching it again.

   WHAT THE BADGE SHOULD SAY

   A strap and its fitted buckle are two Shopify line items joined by a hidden
   _wc_group property. They are one thing to the shopper, so the badge counts
   the strap and ignores the buckle.

   WHAT WAS ALREADY CHECKED, AND IS CORRECT

   The international store does not have this problem, so its theme was
   compared against this one. Everything that fixes it there is already here:

     sections/header.liquid           - computes wc_visible_count, skipping
                                        _wc_role == 'buckle'. Same as intl.
     sections/cart-icon-bubble.liquid - same computation, for the section Dawn
                                        re-renders. Byte-identical to intl.
     assets/wc-cart.js                - asks every cart mutation for a fresh
                                        'cart-drawer,cart-icon-bubble' render
                                        and injects the bubble it gets back.
                                        Byte-identical to intl (20,546 bytes).
     sections/header-group.json       - confirms the header rendered is the
                                        'header' type, i.e. the file above.

   So the server sends the correct number, on first paint and on every cart
   change, by two independent paths. Nothing is missing from the port.

   WHAT IS ACTUALLY WRONG

   Something on the page writes Shopify's raw cart.item_count over the badge
   after the theme has rendered it. It is not the theme; no template here can
   produce that number. The two stores differ only in installed apps, and the
   ones present on India but NOT on international are:

     Rapid Search, KwikPass, Return Prime, Instafeed,
     and a different Foxkit app (foxkit-aio-upsell here vs
     foxkit-upsell-bundles there) — both with enable_ajax_atc on.

   One of those refreshes the badge from /cart.js. To find which: Online Store
   > Themes > Customize > App embeds, and turn them off one at a time. The
   proper fix is that app's own setting, not theme code.

   WHY THIS FILE EXISTS, AND WHY IT IS WRITTEN THIS WAY

   Two earlier versions corrected the badge once, on a schedule, and lost the
   race to whoever wrote last — which is what "it shows 1 then flips to 2"
   means. A single correction is never enough against an overwriter you do not
   control. So this one does not correct once. It holds the value:

     - it corrects EVERY .cart-count-bubble on the page, not just the one
       inside #cart-icon-bubble, in case an app renders its own header copy;
     - it watches the whole body, so it still works if an app replaces the
       cart icon element outright rather than editing it — which orphans an
       observer attached to that element, the flaw in the previous version;
     - it repaints on an animation frame, so a wrong value is never painted;
     - and a plain interval runs regardless of DOM traffic, so nothing can
       starve it the way a debounce gets starved on a busy storefront.

   It only writes when the displayed value is wrong, so a correction cannot
   retrigger itself.

   This is a holding measure. Delete it once no app writes to the badge. */

(function () {
  'use strict';

  if (window.__wcCartCount) return;
  window.__wcCartCount = true;

  var HEARTBEAT_MS = 800;

  var visible = null;   /* strap-only total — what the badge should say */
  var raw = null;       /* Shopify's item_count — what the overwrite writes */
  var frame = null;
  var refreshTimer = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Every badge on the page, wherever it was rendered from. */
  function bubbles() {
    return document.querySelectorAll('.cart-count-bubble');
  }

  function paint() {
    if (visible === null) return;
    var wrong = false;

    bubbles().forEach(function (bubble) {
      var el = bubble.querySelector('span[aria-hidden="true"]');
      if (!el) return;
      var current = parseInt((el.textContent || '').trim(), 10);
      if (current === visible) return;        /* right already — never rewrite */

      el.textContent = String(visible);
      var hidden = bubble.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = visible + ' items';

      /* Only treat it as an overwrite if it was showing the raw total. Any
         other value means the cart genuinely moved and our number is stale. */
      if (current !== raw) wrong = true;
    });

    if (wrong) scheduleRefresh(0);
  }

  function requestPaint() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      try { paint(); } catch (e) { /* no-op */ }
    });
  }

  function refresh() {
    return fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var v = 0;
        cart.items.forEach(function (item) {
          var p = item.properties || {};
          if (p._wc_role === 'buckle') return;
          v += item.quantity;
        });
        visible = v;
        raw = cart.item_count;
        requestPaint();
      })
      .catch(function () { /* leave the badge as the theme rendered it */ });
  }

  function scheduleRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, delay == null ? 120 : delay);
  }

  function start() {
    refresh();

    /* Leading-edge reaction to anything the page does. Coalesced to one
       animation frame, so this stays cheap even on a busy storefront. */
    try {
      new MutationObserver(requestPaint).observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
    } catch (e) { /* no-op */ }

    /* The floor. Independent of DOM traffic, so it cannot be starved. */
    setInterval(function () { paint(); }, HEARTBEAT_MS);

    ['cart:refresh', 'cart:updated', 'wc:cart-synced'].forEach(function (evt) {
      document.addEventListener(evt, function () { scheduleRefresh(); });
    });

    /* Back/forward cache restores a page with a stale cart. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
